import { Bar } from "react-chartjs-2";
import ChartCard from "./ChartsCard";
import { horizontalBarOptions } from "./ChartOptions/horizontalBarChartOptions";
import { useServiceCounter } from "../../hooks/useServiceCounter";
import { useState, useEffect } from "react";

export default function ServiceCountChart() {
  const { years, getServiceCountsForYear, isLoading, SERVICE_LABELS } = useServiceCounter();
  const [selectedYear, setSelectedYear] = useState("");
  const [serviceData, setServiceData] = useState([0, 0, 0, 0, 0, 0]);

  // Custom options for service chart
  const serviceChartOptions = {
    ...horizontalBarOptions,

  };

  // Set default year on load
  useEffect(() => {
    if (years.length > 0 && !selectedYear) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  // Fetch service data when year changes
  useEffect(() => {
    const fetchServiceData = async () => {
      if (selectedYear) {
        try {
          const data = await getServiceCountsForYear(selectedYear);
          setServiceData(data);
        } catch (error) {
          console.error("Error fetching service data:", error);
          setServiceData([0, 0, 0, 0, 0, 0]);
        }
      }
    };

    fetchServiceData();
  }, [selectedYear, getServiceCountsForYear]);

  // Chart data configuration - Single color as requested
  const websiteVisitBarData = {
    labels: SERVICE_LABELS,
    datasets: [
      {
        label: "",
        data: serviceData,
        backgroundColor: [
          "rgba(59, 130, 246, 0.7)",     // Blue - Glovebox Inspection
          "rgba(16, 185, 129, 0.7)",     // Green - Vacuum & Inert Gas
          "rgba(245, 158, 11, 0.7)",     // Amber - Filter & Purification
          "rgba(139, 92, 246, 0.7)",     // Purple - Seal, Gasket & O-Ring
          "rgba(239, 68, 68, 0.7)",      // Red - Sensor Cleaning
          "rgba(14, 165, 233, 0.7)",     // Cyan - Customization & Support
        ],
        borderRadius: 8,
        barThickness: 25,
        borderColor: [
          "rgba(59, 130, 246, 0.7)",     // Blue - Glovebox Inspection
          "rgba(16, 185, 129, 0.7)",     // Green - Vacuum & Inert Gas
          "rgba(245, 158, 11, 0.7)",     // Amber - Filter & Purification
          "rgba(139, 92, 246, 0.7)",     // Purple - Seal, Gasket & O-Ring
          "rgba(239, 68, 68, 0.7)",      // Red - Sensor Cleaning
          "rgba(14, 165, 233, 0.7)",     // Cyan - Customization & Support
        ],
        borderWidth: 1,
        hoverBackgroundColor: "rgba(220, 38, 38, 0.9)",
      },
    ],
  };

  const handleYearChange = (e: any) => {
    setSelectedYear(e.target.value);
  };

  if (isLoading && !selectedYear) {
    return (
      <ChartCard title="Service Interest Distribution">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading service data...</div>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Service Interest Distribution">
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {selectedYear && (
            <div className="text-sm text-gray-600 px-3 py-2">
              <span className="font-medium">Total {selectedYear} Visits:</span>{" "}
              {serviceData.reduce((sum, count) => sum + count, 0)}
            </div>
          )}
          <div className="relative w-full max-w-[90px]">
            <select
              id="service-year-select"
              value={selectedYear}
              onChange={handleYearChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 appearance-none pr-10"
              disabled={years.length === 0}
            >
              {years.length === 0 ? (
                <option value="">No data available</option>
              ) : (
                years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))
              )}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="h-64">
        <Bar
          data={websiteVisitBarData}
          options={horizontalBarOptions}
        />
      </div>
    </ChartCard>
  );
}